import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { debounceTime, map } from "rxjs/operators";
import { Observable } from "rxjs";
import { DealerInfo } from "./oem-dealer.service";
import { VehicleFilter } from "./open.service";
import { DefaultMessage } from "@app/views/private/admin/services/user-manager.service";
import { ResponseSearchAuctions, Vehicle } from "@app-model/vehicle";

@Injectable({
  providedIn: "root"
})
export class DealerService {
  constructor(private http: HttpClient) { }

  putABid(vehicleId: string, bid: number, isAutomaticBid: boolean, automaticBid: number = 0): Observable<BidResult> {
    const params = new HttpParams()
      .set('isAutomaticBid', isAutomaticBid.toString());

    return this.http
      .put<BidResult>(`${environment.api}/dealer/bid/${vehicleId}/${bid}/${automaticBid}`, {}, { params })
      .pipe(map((response: BidResult) => response));
  }

  putFinalBid(vehicleId: string, bid: number): Observable<BidResult> {
    return this.http
      .put(`${environment.api}/dealer/finalBid/${vehicleId}/${bid}`, {})
      .pipe(map((response: BidResult) => response));
  }

  cancelBid(vehicle: Vehicle, reason: string, cancellationNote?: string): Observable<BidResult> {
    return this.http
      .put<BidResult>(`${environment.api}/dealer/cancelBid/${vehicle.id}/${vehicle.currentPrice}/${reason}`, { cancellationNote })
      .pipe(map((response: BidResult) => response));
  }


  saveAsFavorite(vehicleId: string, isFastQuote?: boolean): Observable<any> {
    const url = isFastQuote ? `${environment.api}/dealer/fastQuote/favorite/${vehicleId}` : `${environment.api}/dealer/favorite/${vehicleId}`;
    return this.http.put(url, {});
  }

  removeAsFavorite(vehicleId: string, isFastQuote?: boolean): Observable<any> {
    const url = isFastQuote ? `${environment.api}/dealer/fastQuote/favorite/${vehicleId}` : `${environment.api}/dealer/favorite/${vehicleId}`;
    return this.http.delete(url, {});
  }

  requestJoinExclusiveGroup(vehicleId: string): Observable<any> {
    return this.http.get(
      `${environment.api}/dealer/joinExclusiveGroup/${vehicleId}`, {}
    );
  }

  requestJoinExclusiveGroupFastQuote(fastQuoteId: string): Observable<any> {
    return this.http.get(
      `${environment.api}/dealer/fastQuote/joinExclusiveGroup/${fastQuoteId}`, {}
    );
  }

  addAlternativeBid(
    request: AlternativeBidRequest,
    vehicleId: string
  ): Observable<BidResult> {
    return this.http
      .put(`${environment.api}/dealer/addAlternativeBid/${vehicleId}`, request)
      .pipe(map((response: BidResult) => response));
  }

  acceptCounterOffer(vehicleId: string, request: AlternativeBidRequest): Observable<BidResult> {
    return this.http
      .put(`${environment.api}/dealer/acceptCounterOffer/${vehicleId}`, request)
      .pipe(map((response: BidResult) => response));
  }

  rejectCounterOffer(vehicleId: string, request: AlternativeBidRequest): Observable<BidResult> {
    return this.http
      .put(`${environment.api}/dealer/rejectCounterOffer/${vehicleId}`, request)
      .pipe(map((response: BidResult) => response));
  }

  acceptConditionalCounterOffer(vehicleId: string, conditionalOffer: ConditionalOffer): Observable<BidResult> {
    return this.http
      .put<BidResult>(`${environment.api}/dealer/acceptConditionalCounterOffer/${vehicleId}`, conditionalOffer)
      .pipe(map((response: BidResult) => response));
  }

  rejectConditionalCounterOffer(vehicleId: string, conditionalOffer: ConditionalOffer): Observable<BidResult> {
    return this.http
      .put<BidResult>(`${environment.api}/dealer/rejectConditionalCounterOffer/${vehicleId}`, conditionalOffer)
      .pipe(map((response: BidResult) => response));
  }

  createFilterAlert(alert: DealerAlert): Observable<DefaultMessage> {
    return this.http
      .post(`${environment.api}/dealer/alert`, alert)
      .pipe(map((response: DefaultMessage) => response));
  }

  getAlerts(): Observable<DealerAlert[]> {
    return this.http
      .get(`${environment.api}/dealer/alerts`)
      .pipe(map((response: DealerAlert[]) => response.map(a => {
        a.filter = Object.assign(new VehicleFilter(), a.filter);
        return a;
      })));
  }

  deleteAlert(id: string): Observable<DefaultMessage> {
    return this.http
      .delete(`${environment.api}/dealer/alert/${id}`)
      .pipe(map((response: DefaultMessage) => response));
  }

  updateAlertStatus(id: string, inactive: boolean): Observable<DefaultMessage> {
    return this.http
      .patch(`${environment.api}/dealer/alert/${id}/${inactive}`, {})
      .pipe(map((response: DefaultMessage) => response));
  }


  acceptOptin(appClientId: string): Observable<DefaultMessage> {
    return this.http
      .post<DefaultMessage>(`${environment.api}/dealer/acceptOptin?appClientId=${appClientId}`, {});
  }

  cancelAutomaticBid(id: string, automaticBidValue: number): Observable<DefaultMessage> {
    return this.http.put<DefaultMessage>(`${environment.api}/dealer/cancelAutomaticBid/${id}/${automaticBidValue}`, {});
  }

  searchMyProposals(filter: VehicleFilter): Observable<ResponseSearchAuctions> {
    return this.http.post<ResponseSearchAuctions>(`${environment.api}/dealer/my-proposals`, filter)
      .pipe(
        debounceTime(100),
        map((response: ResponseSearchAuctions) => {
          const data: Vehicle[] = [];
          response.data.forEach((d) =>
            data.push(Object.assign(new Vehicle(), d))
          );
          response.data = data;
          return response;
        })
      );
  }

  searchFavorites(filter: VehicleFilter): Observable<ResponseSearchAuctions> {
    return this.http.post<ResponseSearchAuctions>(`${environment.api}/dealer/my-favorites`, filter)
      .pipe(
        debounceTime(100),
        map((response: ResponseSearchAuctions) => {
          const data: Vehicle[] = [];
          response.data.forEach((d) =>
            data.push(Object.assign(new Vehicle(), d))
          );
          response.data = data;
          return response;
        })
      );
  }

  migrationNotificationCheckout(): Observable<DefaultMessage> {
    return this.http.post<DefaultMessage>(`${environment.api}/dealer/checkCheckoutMigrationNotification`, {});
  }

}

export enum BidStatus {
  OK = "ok",
  NO_AUCTION = "Veículo não está disponível para ofertas",
  NO_VEHICLE = "Veículo não encontrado",
  SOLD = "Este veículo já foi vendido",
  EXPIRED = "O tempo de negociação deste veículo expirou",
  NOT_A_DEALER = "Você não tem acesso a este veículo",
  LESS_THAN_MIN_BID = "O valor está abaixo da oferta mínima",
  LESS_THAN_FINAL_BID = "Valor abaixo da última oferta. Atualize a página e tente novamente.",
  ALTERNATIVE_BID_NOT_AVAILABLE = "Não é possível enviar ofertas personalizadas para esta negociação.",
  ALTERNATIVE_BID_TOO_HIGH = "Valor acima da oferta mínima. Por este valor você pode fazer uma oferta neste veículo.",
  EXISTING_ALTERNATIVE_BID = "Você já enviou uma proposta personalizada com esse valor. Aguarde a resposta da concessionária.",
  NOT_OEM_DEALER = "Você não tem acesso a este veículo",
  INCORRET_BID_STEP_VALUE = "Valor muito baixo para os steps das ofertas.",
  INITIAL_BID_CANT_BE_EMPTY = "Valor inicial é obrigatório.",
  FINISH_VALUE_CANT_BE_EMPTY = "Valor para compre já é obrigatório.",
  NO_OFFER_TYPE_SELECTED = "Selecione pelo menos um tipo de oferta",
  CANT_CHANGE_CONDITIONS = "Esta negociação não pode ser alterada pois já está em andamento.",
  CANT_BID_ON_OWN_VEHICLE = "Não é possível fazer uma oferta nos seus próprios veículos.",
  CANT_BID = "Não é possível fazer esta oferta neste veículo.",
  LATE_PAYMENT = "Você possui boletos atrasados. Regularize seus pagamentos para poder fazer ofertas novamente.",
  ONLY_PJ = "Negociação disponível apenas para Pessoa Jurídica.",
  ONLY_PJ_CNAE = "Negociação disponível apenas para Pessoa Jurídica com CNAE PERMITIDO.",
  CANT_MAKE_COUNTER_OFFER = 'Não é possível fazer uma contra oferta.',
  COUNTER_OFFER_TOO_HIGH = 'Contra proposta deve ser menor do que o valor da oferta mínima.',
  NO_COUNTER_OFFER = 'Contra proposta não encontrada',
  COUNTER_OFFER_NOT_AVAILABLE = 'Esta contraproposta não está mais disponível.',
  INACTIVE_GROUP = 'O grupo exclusivo selecionado está inativo.',
  NOT_AUTHORIZED = 'Você não tem nível de acesso suficiente para esta ação',
  BID_NOT_FOUND = 'Oferta não encontrada. Por favor, atualize a página.',
  CANT_CANCEL_BID = 'Não foi possível cancelar esta oferta. Por favor, atualize a página.',
  BID_CANCELLATION_NOTE_REQUIRED = 'Para o motivo "Outro" é obrigatório descrever o motivo do cancelamento.',
  GENERATING_BANK_SLIP = 'Não é possível fazer uma nova oferta neste veículo. Existe uma compra sendo finalizada neste momento.',
  NOT_SUPER_BID_ACCOUNT = 'O comprador não tem conta SBX. Quando sua conta for ativada, essa oferta será aceita automaticamente.',
  INACTIVE_SUPER_BID_ACCOUNT = 'O comprador não tem conta SBX ativa. Tente novamente após ativação da conta.',
  CANNOT_CHANGE_EXPIRATION_HAS_BID = 'Não é possível alterar a data de encerramento pois já existe uma oferta neste veículo.',
  OFFERS_ALREADY_EXITS = "Já existe uma oferta para este veículo.",
  INVALID_BID_OPTION = "Tipo de proposta inválido.",
  NOT_AN_EXCLUSIVE_DEALER = "Você não tem acesso a este grupo exclusivo.",
  FINISH_DATE_TOO_SOON = "O tempo mínimo de exposição de uma oferta é de 5 minutos.",
  INACTIVE_DEALER = "O comprador está inativado ou bloqueado na plataforma. Não é possível registrar proposta de venda direta para ele no momento.",
  BELOW_MINIMUM_MARGIN = "A proposta está abaixo da margem mínima configurada.",
  INACTIVE = "Esta cotação rápida está inativa."
}

export interface BidResult {
  success: boolean;
  status: BidStatus;
  effectiveMarginLimit?: number | null;
}

export interface BidCancellationRequest {
  cancellationNote?: string;
}

export class AlternativeBidRequest {
  value: number;
  message: string;
  dealerInfo: DealerInfo;
  dealerId: string;
  createdAt: string;
  statusChangedAt: string;
  status?: string;
  counterOffer: CounterOffer;
  finishDate: string;
}

export class CounterOffer {
  value: number;
  message: string;
  createdAt: Date;
  createdBy: string;
  status: 'WAITING' | 'REJECTED' | 'ACCEPTED' | 'CANCELED';
  statusChangedAt: Date;

  static getStatusLabel(status: string) {
    switch (status) {
      case 'WAITING': return 'Aguardando resposta';
      case 'REJECTED': return 'Rejeitada';
      case 'ACCEPTED': return 'Aceita';
      case 'CANCELED': return 'Cancelada';
      default: return status;
    }
  }
}

export class DealerAlert {
  id: string;
  name: string;
  filter: VehicleFilter;
  inactive: boolean;
  createdAt: Date;
  createdBy: string;
}

export enum CancelBidReason {
  MISTAKE = 'Mandei errado',
  BOUGHT_SIMILAR = 'Arrematei outro veículo similar',
  LOST_INTEREST = 'Não tenho mais interesse',
  OTHER = 'Outro'
}

export class ConditionalOffer {
  updatedAt: Date;
  updatedBy: string;
  createdAt: Date;
  status: string;
  counterOffer: CounterOffer;
  bid: number;
  dealerId: string;
  dealerName: string
}